import { buildVehicleFilterUrl, displayLabelForVehicle, loadVehicle } from "@theme/kenteken-api";
import { cloneSlideForLoop, setupProductSliderDrag, snapScrollToStep } from "@theme/vp-product-slider-drag";
//#region assets/model-product-slider.ts
/**
* Model product slider — brand toggle, infinite scroll, drag/swipe, and vehicle-filtered products.
*/
var PRODUCTS_SECTION_ID = "model-product-slider-products";
/**
* @param {string} merk
* @returns {'bmw'|'mini'|null}
*/
function brandKeyFromMerk(merk) {
	const normalized = String(merk || "").trim().toLowerCase();
	if (normalized.includes("mini")) return "mini";
	if (normalized.includes("bmw")) return "bmw";
	return null;
}
/**
* @param {import('@theme/kenteken-api').VpVehicle|null|undefined} vehicle
* @returns {boolean}
*/
function hasVehicleFilters(vehicle) {
	if (!vehicle) return false;
	return Boolean(vehicle.brandAndModel || vehicle.brandAndModels?.length);
}
var ModelProductSlider = class extends HTMLElement {
	/** @type {AbortController|null} */
	#productsAbort = null;
	/** @type {WeakMap<HTMLElement, number>} */
	#setWidths = /* @__PURE__ */ new WeakMap();
	/** @type {(() => void)|null} */
	#teardownDrag = null;
	connectedCallback() {
		this.toggleButtons = this.querySelectorAll("[data-brand]");
		this.panels = this.querySelectorAll("[data-brand-panel]");
		this.prevButton = this.querySelector("[data-prev]");
		this.nextButton = this.querySelector("[data-next]");
		this.clip = this.querySelector(".model-product-slider__clip");
		this.fitRoot = this.querySelector("[data-vp-slider-fit]");
		this.fitLabel = this.querySelector("[data-vp-slider-fit-label]");
		this.titleEl = this.querySelector(".section-title__title");
		this.titleDefault = this.dataset.titleDefault || "";
		this.titleMatched = this.dataset.titleMatched || "";
		this.offsets = /* @__PURE__ */ new WeakMap();
		this.defaultTracks = /* @__PURE__ */ new Map();
		this.activeBrand = this.dataset.defaultBrand || "mini";
		this.collectionUrl = this.dataset.collectionUrl || "/collections/all";
		this.productsToShow = Number.parseInt(this.dataset.productsToShow || "6", 10) || 6;
		this.productsSectionId = this.dataset.productsSectionId || PRODUCTS_SECTION_ID;
		/** When set, always keep Liquid-rendered override collection; ignore vehicle/model filters. */
		this.overrideCollection = this.dataset.overrideCollection === "true";
		this.panels.forEach((panel) => {
			const track = panel.querySelector("[data-track]");
			if (track && panel instanceof HTMLElement) this.defaultTracks.set(panel.dataset.brandPanel || "", track.innerHTML);
		});
		this.toggleButtons.forEach((button) => {
			button.addEventListener("click", () => this.setBrand(button.dataset.brand));
		});
		if (this.prevButton) this.prevButton.addEventListener("click", () => this.scrollByDirection(-1));
		if (this.nextButton) this.nextButton.addEventListener("click", () => this.scrollByDirection(1));
		this.panels.forEach((panel) => {
			const track = panel.querySelector("[data-track]");
			if (track instanceof HTMLElement) track.addEventListener("transitionend", (event) => {
				if (event.target !== track || event.propertyName !== "transform") return;
				this.normalizeLoopOffset(track);
			});
		});
		if (this.clip instanceof HTMLElement) this.#teardownDrag = setupProductSliderDrag({
			clip: this.clip,
			getTrack: () => this.activeTrack,
			usesTransform: () => this.usesTransform,
			getOffset: (track) => this.getOffset(track),
			setOffset: (track, offset, options) => this.setOffset(track, offset, options),
			getMaxOffset: (track) => this.getMaxOffset(track),
			getStep: (track) => this.getStep(track),
			snapTransformOffset: (track) => this.snapOffset(track),
			snapScrollOffset: (track) => this.snapScrollOffset(track),
			trackCanScroll: (track) => this.trackCanScroll(track),
			navSelector: ".model-product-slider__nav",
			onDragMove: () => {
				if (!this.usesTransform) this.wrapMobileScroll();
			},
			onDragEnd: () => {
				this.updateNavVisibility();
			}
		});
		this.syncFitLabel();
		this.syncVehicleProducts();
		window.addEventListener("vp-vehicle:change", this.onVehicleChange);
		this.clip?.addEventListener("scroll", this.onClipScroll, { passive: true });
		window.addEventListener("resize", this.onResize, { passive: true });
	}
	disconnectedCallback() {
		this.#productsAbort?.abort();
		this.#teardownDrag?.();
		this.#teardownDrag = null;
		this.clip?.removeEventListener("scroll", this.onClipScroll);
		window.removeEventListener("vp-vehicle:change", this.onVehicleChange);
		window.removeEventListener("resize", this.onResize);
	}
	onVehicleChange = () => {
		this.syncFitLabel();
		this.syncVehicleProducts();
	};
	onResize = () => {
		const track = this.activeTrack;
		if (track instanceof HTMLElement) {
			const wasLooping = track.dataset.loop === "true";
			const count = Number.parseInt(track.dataset.originalCount || "0", 10);
			const realSlides = [...track.querySelectorAll(".model-product-slider__slide")].slice(0, count || void 0);
			const last = realSlides[realSlides.length - 1];
			if (wasLooping !== (Boolean(this.clip) && realSlides.length >= 2 && last != null && last.offsetLeft + last.offsetWidth > (this.clip?.clientWidth || 0) + 4)) {
				const offset = this.getOffset(track);
				this.prepareTrack(track);
				if (offset) this.setOffset(track, offset, { animate: false });
			} else this.measureSetWidth(track);
		}
		this.clampActiveOffset();
		this.updateNavVisibility();
	};
	onClipScroll = () => {
		this.wrapMobileScroll();
		this.updateNavVisibility();
	};
	/**
	* Snap transform offset to the nearest card step after a drag.
	* @param {HTMLElement} track
	*/
	snapOffset(track) {
		const step = this.getStep(track);
		if (!step) return;
		let offset = this.getOffset(track);
		const looping = track.dataset.loop === "true";
		const setWidth = this.getSetWidth(track);
		if (looping && setWidth > 0) {
			if (offset < 0) {
				this.setOffset(track, offset + setWidth, { animate: false });
				offset = this.getOffset(track);
			} else if (offset >= setWidth) {
				this.setOffset(track, offset - setWidth, { animate: false });
				offset = this.getOffset(track);
			}
		}
		offset = Math.round(offset / step) * step;
		if (!looping) offset = Math.max(0, Math.min(offset, this.getMaxOffset(track)));
		this.setOffset(track, offset, { animate: true });
	}
	/**
	* Snap native scroll position after a mobile drag.
	* @param {HTMLElement} track
	*/
	snapScrollOffset(track) {
		if (!this.clip) return;
		snapScrollToStep(this.clip, track, (activeTrack) => this.getStep(activeTrack));
	}
	/**
	* Start on product 2 so the rail bleeds full-width (peek left + right).
	* @param {HTMLElement} track
	*/
	applyStartOffset(track) {
		if (!(track instanceof HTMLElement) || !this.clip) return;
		if (!this.trackCanScroll(track)) return;
		const step = this.getStep(track);
		if (!step) return;
		if (this.usesTransform) this.setOffset(track, step, { animate: false });
		else this.clip.scrollLeft = step;
	}
	/**
	* Keep native mobile scrolling inside the real slide set when clones are present.
	*/
	wrapMobileScroll() {
		if (this.usesTransform || !this.clip) return;
		const track = this.activeTrack;
		if (!(track instanceof HTMLElement) || track.dataset.loop !== "true") return;
		const setWidth = this.getSetWidth(track);
		if (setWidth <= 0) return;
		const { scrollLeft } = this.clip;
		if (scrollLeft >= setWidth - 1) this.clip.scrollLeft = scrollLeft - setWidth;
		else if (scrollLeft <= 0 && this.clip.scrollWidth > this.clip.clientWidth + 4) {}
	}
	syncFitLabel() {
		if (this.overrideCollection) {
			this.classList.remove("has-vehicle");
			return;
		}
		const vehicle = loadVehicle();
		const vehicleLabel = vehicle ? displayLabelForVehicle(vehicle) : "";
		const hasMatch = Boolean(vehicleLabel);
		this.classList.toggle("has-vehicle", hasMatch);
		if (this.fitRoot && this.fitLabel) {
			if (hasMatch) {
				this.fitLabel.textContent = `Past op uw ${vehicleLabel}`;
				this.fitRoot.hidden = false;
			} else {
				this.fitLabel.textContent = "";
				this.fitRoot.hidden = true;
			}
		}
		if (this.titleEl instanceof HTMLElement) {
			const nextTitle = hasMatch && this.titleMatched ? this.titleMatched : this.titleDefault;
			if (nextTitle) this.titleEl.textContent = nextTitle;
		}
	}
	async syncVehicleProducts() {
		if (this.overrideCollection) {
			const track = this.activeTrack;
			if (track instanceof HTMLElement) {
				this.prepareTrack(track);
				this.applyStartOffset(track);
				this.updateNavVisibility();
			}
			return;
		}
		this.#productsAbort?.abort();
		const abortController = new AbortController();
		this.#productsAbort = abortController;
		const vehicle = loadVehicle();
		if (!hasVehicleFilters(vehicle)) {
			this.restoreDefaultTracks();
			if (abortController.signal.aborted) return;
			this.setBrand(this.dataset.defaultBrand || this.activeBrand || "mini");
			return;
		}
		const brandKey = brandKeyFromMerk(vehicle?.merk) || this.dataset.defaultBrand || "mini";
		this.setBrand(brandKey, { preserveOffset: true });
		try {
			const slides = await this.#fetchFilteredSlides(vehicle, abortController.signal);
			if (abortController.signal.aborted) return;
			const currentTrack = this.querySelector(`[data-brand-panel="${brandKey}"]`)?.querySelector("[data-track]");
			if (!(currentTrack instanceof HTMLElement)) return;
			currentTrack.innerHTML = "";
			slides.forEach((slide) => currentTrack.append(slide));
			this.prepareTrack(currentTrack);
			this.offsets.delete(currentTrack);
			currentTrack.style.transform = "";
			if (this.clip) this.clip.scrollLeft = 0;
			this.applyStartOffset(currentTrack);
			this.updateNavVisibility();
		} catch (error) {
			if (error?.name === "AbortError") return;
		}
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @param {AbortSignal} signal
	* @returns {Promise<Element[]>}
	*/
	async #fetchFilteredSlides(vehicle, signal) {
		const slides = await this.#fetchSlidesForVehicle(vehicle, signal);
		if (slides.length || signal.aborted) return slides;
		return this.#fetchSlidesForVehicle(vehicle, signal, { merkOnly: true });
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @param {AbortSignal} signal
	* @param {{ merkOnly?: boolean }} [options]
	* @returns {Promise<Element[]>}
	*/
	async #fetchSlidesForVehicle(vehicle, signal, options = {}) {
		const filterUrl = buildVehicleFilterUrl(vehicle, this.collectionUrl, options);
		const url = new URL(filterUrl, window.location.origin);
		url.searchParams.set("section_id", this.productsSectionId);
		const response = await fetch(url.toString(), {
			method: "GET",
			headers: { Accept: "text/html" },
			signal
		});
		if (!response.ok) throw new Error("Unable to load model products");
		const html = await response.text();
		if (signal.aborted) return [];
		const nextTrack = new DOMParser().parseFromString(html, "text/html").querySelector("[data-track]");
		if (!(nextTrack instanceof HTMLElement)) return [];
		return [...nextTrack.querySelectorAll(".model-product-slider__slide")].slice(0, this.productsToShow);
	}
	restoreDefaultTracks() {
		this.panels.forEach((panel) => {
			const brand = panel.dataset.brandPanel || "";
			const track = panel.querySelector("[data-track]");
			const defaultHtml = this.defaultTracks.get(brand);
			if (track instanceof HTMLElement && defaultHtml != null) {
				track.innerHTML = defaultHtml;
				track.dataset.loop = "false";
				delete track.dataset.originalCount;
				this.#setWidths.delete(track);
				this.offsets.delete(track);
				track.style.transform = "";
				track.classList.remove("is-jumping");
			}
		});
		const activeTrack = this.activeTrack;
		if (activeTrack instanceof HTMLElement) {
			this.prepareTrack(activeTrack);
			this.applyStartOffset(activeTrack);
		}
		if (this.clip && !this.usesTransform) {
			if (!activeTrack || !this.trackCanScroll(activeTrack)) this.clip.scrollLeft = 0;
		}
		this.updateNavVisibility();
	}
	/**
	* Strip previous clones and rebuild a single duplicate set for looping when needed.
	* @param {HTMLElement} track
	*/
	prepareTrack(track) {
		track.querySelectorAll("[data-clone=\"true\"]").forEach((clone) => clone.remove());
		track.classList.remove("is-jumping");
		this.#setWidths.delete(track);
		const slides = [...track.querySelectorAll(".model-product-slider__slide:not([data-clone])")];
		track.dataset.originalCount = String(slides.length);
		track.dataset.loop = "false";
		if (slides.length < 2 || !this.clip) return;
		track.offsetWidth;
		const last = slides[slides.length - 1];
		if (last.offsetLeft + last.offsetWidth <= this.clip.clientWidth + 4) return;
		slides.forEach((slide) => track.append(cloneSlideForLoop(slide)));
		track.dataset.loop = "true";
		this.measureSetWidth(track);
	}
	/**
	* @param {HTMLElement} track
	*/
	measureSetWidth(track) {
		const count = Number.parseInt(track.dataset.originalCount || "0", 10);
		if (!count) {
			this.#setWidths.delete(track);
			return;
		}
		const slides = [...track.querySelectorAll(".model-product-slider__slide")];
		if (slides.length < count * 2) {
			this.#setWidths.delete(track);
			return;
		}
		const first = slides[0];
		const setWidth = slides[count].offsetLeft - first.offsetLeft;
		if (setWidth > 0) this.#setWidths.set(track, setWidth);
	}
	/**
	* @param {HTMLElement} track
	* @returns {number}
	*/
	getSetWidth(track) {
		if (!this.#setWidths.has(track)) this.measureSetWidth(track);
		return this.#setWidths.get(track) || 0;
	}
	get activeTrack() {
		return this.querySelector(".model-product-slider__panel.is-active [data-track]");
	}
	get usesTransform() {
		return window.matchMedia("(min-width: 750px)").matches;
	}
	getOffset(track) {
		return this.offsets.get(track) || 0;
	}
	setOffset(track, offset, { animate = true } = {}) {
		this.offsets.set(track, offset);
		if (!animate) track.classList.add("is-jumping");
		track.style.transform = offset ? `translate3d(-${offset}px, 0, 0)` : "";
		if (!animate) {
			track.offsetWidth;
			track.classList.remove("is-jumping");
		}
	}
	getMaxOffset(track) {
		if (!track || !this.clip) return 0;
		if (track.dataset.loop === "true") {
			const setWidth = this.getSetWidth(track);
			return setWidth > 0 ? setWidth : Math.max(0, track.scrollWidth - this.clip.clientWidth);
		}
		return Math.max(0, track.scrollWidth - this.clip.clientWidth);
	}
	getStep(track) {
		const slide = track.querySelector(".model-product-slider__slide");
		if (!slide) return 0;
		const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
		return slide.getBoundingClientRect().width + gap;
	}
	/**
	* Keep the transform inside the first (real) slide set after a loop transition.
	* @param {HTMLElement} track
	*/
	normalizeLoopOffset(track) {
		if (track.dataset.loop !== "true" || !this.usesTransform) return;
		const setWidth = this.getSetWidth(track);
		if (setWidth <= 0) return;
		let offset = this.getOffset(track);
		if (offset < 0 || offset >= setWidth) {
			offset = (offset % setWidth + setWidth) % setWidth;
			this.setOffset(track, offset, { animate: false });
		}
	}
	clampActiveOffset() {
		const track = this.activeTrack;
		if (!track || !this.usesTransform) return;
		if (track.dataset.loop === "true") {
			this.normalizeLoopOffset(track);
			return;
		}
		const maxOffset = this.getMaxOffset(track);
		const nextOffset = Math.min(this.getOffset(track), maxOffset);
		this.setOffset(track, nextOffset, { animate: false });
	}
	/**
	* @param {string|undefined} brand
	* @param {{ preserveOffset?: boolean }} [options]
	*/
	setBrand(brand, options = {}) {
		if (!brand) return;
		this.activeBrand = brand;
		this.toggleButtons.forEach((button) => {
			const isActive = button.dataset.brand === brand;
			button.classList.toggle("is-active", isActive);
			button.setAttribute("aria-pressed", isActive ? "true" : "false");
		});
		this.panels.forEach((panel) => {
			const isActive = panel.dataset.brandPanel === brand;
			panel.classList.toggle("is-active", isActive);
			panel.hidden = !isActive;
		});
		if (!options.preserveOffset && this.clip) {
			this.clip.scrollLeft = 0;
			const track = this.activeTrack;
			if (track instanceof HTMLElement) {
				this.offsets.delete(track);
				track.style.transform = "";
				track.classList.remove("is-jumping");
			}
		}
		const activeTrack = this.activeTrack;
		if (activeTrack instanceof HTMLElement) {
			this.prepareTrack(activeTrack);
			if (!options.preserveOffset) this.applyStartOffset(activeTrack);
		}
		this.updateNavVisibility();
	}
	/**
	* @param {-1|1} direction
	*/
	scrollByDirection(direction) {
		const track = this.activeTrack;
		if (!track) return;
		const step = this.getStep(track);
		if (!step) return;
		if (!this.usesTransform) {
			const scroller = this.clip || track;
			if (scroller.scrollWidth - scroller.clientWidth <= 4) return;
			let nextLeft = scroller.scrollLeft + direction * step;
			if (track.dataset.loop === "true") {
				const setWidth = this.getSetWidth(track);
				if (setWidth > 0) {
					if (direction > 0 && nextLeft >= setWidth - 4) {
						scroller.scrollLeft = nextLeft - setWidth;
						nextLeft = scroller.scrollLeft + step;
					} else if (direction < 0 && nextLeft < 4) {
						scroller.scrollLeft = nextLeft + setWidth;
						nextLeft = scroller.scrollLeft - step;
					}
				}
			}
			scroller.scrollTo({
				left: nextLeft,
				behavior: "smooth"
			});
			return;
		}
		let offset = this.getOffset(track);
		const setWidth = this.getSetWidth(track);
		if (track.dataset.loop === "true" && setWidth > 0 && direction < 0 && offset < 4) {
			this.setOffset(track, offset + setWidth, { animate: false });
			offset = this.getOffset(track);
			requestAnimationFrame(() => {
				this.setOffset(track, offset + direction * step);
				this.updateNavVisibility();
			});
			return;
		}
		this.setOffset(track, offset + direction * step);
		this.updateNavVisibility();
	}
	updateNavVisibility() {
		const track = this.activeTrack;
		if (!track) return;
		const canScroll = this.trackCanScroll(track);
		if (this.prevButton) this.prevButton.hidden = !canScroll;
		if (this.nextButton) this.nextButton.hidden = !canScroll;
	}
	/**
	* @param {HTMLElement} track
	* @returns {boolean}
	*/
	trackCanScroll(track) {
		if (!this.clip) return false;
		if (track.dataset.loop === "true") return this.getSetWidth(track) > 4;
		if (this.usesTransform) return this.getMaxOffset(track) > 4;
		return this.clip.scrollWidth - this.clip.clientWidth > 4;
	}
};
if (!customElements.get("model-product-slider")) customElements.define("model-product-slider", ModelProductSlider);
//#endregion
